import { ark } from "@ark-ui/react/factory";
import { cva, cx, type RecipeVariantProps } from "styled-system/css";
import { Token, token } from "styled-system/tokens";

import { NumberFormatter } from "@/utils/number-formatter";

type PositiveNegativeSize = NonNullable<RecipeVariantProps<typeof positiveNegativeRecipe>>["size"];

export type NumericFormatter =
  | "usdc"
  | "price"
  | "compact"
  | "compactHeading"
  | "percent"
  | "percentFraction"
  | "ratio"
  | "compactCurrencyHeading";

export interface PositiveNegativeProps {
  /** The value to be displayed */
  value?: number;
  /** The text to be displayed */
  children?: React.ReactNode;
  /** The size of the text */
  size?: PositiveNegativeSize;
  /** Whether to render as the child element instead of as a span */
  asChild?: boolean;
  /** The class name to be applied to the component */
  className?: string;
  /** The formatter to be applied to the value */
  formatter?: NumericFormatter | ((value: number) => string);
  /** Whether the value is positive, overrides the value. Pass null to force the neutral color regardless of value. */
  isPositive?: boolean | null;
  /** Whether to show the sign of the value */
  showSign?: boolean;
  /** The prefix to be applied to the value */
  prefix?: string;
  /** The suffix to be applied to the value */
  suffix?: string;
  /** The neutral color to use if not positive/negative */
  neutralColor?: Token;
  /** override the color of the text */
  overrideColor?: Token;
}

const formatters: Record<NumericFormatter, (value: number) => string> = {
  usdc: (value) => NumberFormatter.usdc(value),
  price: (value) => NumberFormatter.price(value),
  compactCurrencyHeading: (value) => NumberFormatter.compactHeading(value, { currency: true }),
  compact: (value) => NumberFormatter.compact(value),
  compactHeading: (value) => NumberFormatter.compactHeading(value),
  percent: (value) => NumberFormatter.percent(value),
  ratio: (value) => NumberFormatter.ratio(value),
  percentFraction: (value) => NumberFormatter.percent(value, { isFraction: false }),
};

const getState = (value: number, isPositive?: boolean | null) => {
  if (isPositive === null) return undefined;

  if (isPositive !== undefined) {
    return isPositive ? "positive" : "negative";
  }

  if (value > 0) return "positive";

  if (value < 0) return "negative";

  return undefined;
};

const getOverrideColor = (
  neutralColor: Token,
  overrideColor?: Token,
  state?: "positive" | "negative",
) => {
  if (overrideColor) return token(overrideColor);

  if (!state) return token(neutralColor);

  return undefined;
};

const getSign = (value: number | undefined, showSign?: boolean) => {
  if (!showSign || !value) return "";
  if (value > 0) return "+";
  return "";
};

export const PositiveNegative = ({
  value,
  children,
  size,
  asChild,
  className,
  isPositive,
  formatter = "price",
  showSign,
  prefix,
  suffix,
  neutralColor = "colors.text.tertiary",
  overrideColor,
}: PositiveNegativeProps) => {
  const formatterFn = typeof formatter === "function" ? formatter : formatters[formatter];
  const state = getState(value ?? 0, isPositive);

  return (
    <ark.span
      asChild={asChild}
      className={cx(positiveNegativeRecipe({ state, size }), className)}
      style={{ color: getOverrideColor(neutralColor, overrideColor, state) }}
    >
      {prefix}
      {children ?? `${getSign(value, showSign)}${formatterFn(value ?? 0)}`}
      {suffix}
    </ark.span>
  );
};

const positiveNegativeRecipe = cva({
  base: {},
  variants: {
    state: {
      positive: {
        color: "text.positive",
      },
      negative: {
        color: "text.negative",
      },
    },
    size: {
      sm: {
        textStyle: "body.sm",
      },
      md: {
        textStyle: "body.md",
      },
      lg: {
        textStyle: "body.lg",
      },
    },
  },
});
